import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { useSession } from "@/hooks/use-session";
import { usePaidMediaOverviewV2 } from "@/hooks/use-paid-media-overview-v2";
import {
  OverviewV2ApiError,
  type OverviewV2ContextParams,
} from "@/apis/brand-os/paid-media-overview-v2";

import { SectionShell, SectionErrorState } from "./section-shell";
import { KpiSnapshot, KpiSnapshotSkeleton } from "./kpi-snapshot";
import { TopMovers, TopMoversSkeleton } from "./top-movers";
import { InsightsSection } from "./insights-section";
import { ExternalSignals, ExternalSignalsSkeleton } from "./external-signals";
import { formatRelativeUpdated } from "./format";

type Session = ReturnType<typeof useSession>;

/**
 * Temporary kill-switches for the data sections of the overview page.
 *
 * - `SHOW_DATA_SECTIONS` — when `false`, skips the combined `/` fetch
 *   and renders only Insights. When `true`, restores KPI snapshot + external
 *   signals (and Top movers if `SHOW_TOP_MOVERS` is on).
 * - `SHOW_TOP_MOVERS` — finer-grained toggle for the right column inside
 *   the data layout. Has no effect while `SHOW_DATA_SECTIONS` is off.
 *
 * Both keep the supporting components and hooks wired up so re-enabling
 * is a one-line change.
 */
const SHOW_DATA_SECTIONS = true;
const SHOW_TOP_MOVERS = false;

/**
 * Paid Media Overview v2 — full page.
 *
 * Layout modes via `SHOW_DATA_SECTIONS` / `SHOW_TOP_MOVERS`:
 *
 *   Data ON — KPI snapshot (+ optional Top movers) + Insights + External signals.
 *             Combined `GET /` on mount feeds KPI + external (+ movers when enabled).
 *   Data OFF — Insights-only surface; no combined fetch.
 *
 * The Insights & Recommendations card is user-driven via `POST /insights/run`
 * — no auto-fire on mount in either mode (would cost an LLM call per view).
 */
export function PaidMediaOverviewV2Page() {
  const { user, organization } = useSession();

  if (!SHOW_DATA_SECTIONS) {
    return (
      <PageFrame>
        <InsightsSection
          userId={user.id}
          organizationId={organization.id}
          userEmail={user.email}
        />
      </PageFrame>
    );
  }

  return <PaidMediaOverviewV2DataPage user={user} organization={organization} />;
}

/**
 * Full overview page (snapshot + movers + insights + external signals).
 * Split into its own component so `usePaidMediaOverviewV2` is only called
 * — and its network request only fired — when the data sections are on.
 *
 * Failure handling:
 *   - 503 NO_PLATFORM_AVAILABLE → full-page "Connect an ad account" empty state.
 *   - Combined endpoint succeeds with a section `null` → render that one
 *     section's compact retry placeholder; rest of the page is fine.
 *   - Per-section refresh fails → leaves cached data alone, the section's
 *     refresh button shows a brief loading state and then resets.
 */
function PaidMediaOverviewV2DataPage({
  user,
  organization,
}: {
  user: Session["user"];
  organization: Session["organization"];
}) {
  const overview = usePaidMediaOverviewV2(OVERVIEW_V2_PARAMS);

  // --- 503 NO_PLATFORM_AVAILABLE: page-level empty state --------------------
  if (overview.error instanceof OverviewV2ApiError && overview.error.errorCode === "NO_PLATFORM_AVAILABLE") {
    return (
      <PageFrame>
        <NoPlatformEmptyState message={overview.error.message} />
      </PageFrame>
    );
  }

  // --- generic page-level error (still let user retry) ----------------------
  if (overview.error && !overview.summary) {
    return (
      <PageFrame>
        <Card>
          <CardContent className="space-y-3 p-6 text-sm">
            <p className="text-foreground font-medium">Couldn't load the overview.</p>
            <p className="text-muted-foreground">{overview.error.message}</p>
            <Button size="sm" variant="outline" onClick={() => overview.refetch()}>
              <RefreshCw className="size-3.5" /> Try again
            </Button>
          </CardContent>
        </Card>
      </PageFrame>
    );
  }

  // --- pending: show full skeleton ------------------------------------------
  if (overview.isLoading || !overview.summary) {
    return (
      <PageFrame>
        <TopRow
          left={
            <SectionShell title="Performance snapshot">
              <KpiSnapshotSkeleton />
            </SectionShell>
          }
          right={
            SHOW_TOP_MOVERS ? (
              <SectionShell title="Top movers">
                <TopMoversSkeleton />
              </SectionShell>
            ) : null
          }
        />
        <Separator />
        {/* Insights surface is user-driven — render it eagerly so the
            user can start composing while snapshot/top-movers are
            still in flight. */}
        <InsightsSection
          userId={user.id}
          organizationId={organization.id}
          userEmail={user.email}
        />
        <Separator />
        <SectionShell
          title="External signals"
          rightLabel="factors outside your campaigns that can move your numbers"
        >
          <ExternalSignalsSkeleton />
        </SectionShell>
      </PageFrame>
    );
  }

  const { summary } = overview;
  const errorsBySection = new Map(summary.errors.map((e) => [e.section, e]));

  return (
    <PageFrame
      syncedAt={summary.synced_at}
      isFetching={overview.isFetching}
      onRefetch={overview.refreshAll}
    >
      {/* --- Top row: snapshot (left) + movers (right, optional) -------- */}
      <TopRow
        left={
          <SectionShell
            title="Performance snapshot"
            syncedAt={summary.snapshot?.synced_at ?? summary.synced_at}
            refreshing={overview.snapshot.refreshing}
            onRefresh={overview.snapshot.refresh}
          >
            {summary.snapshot ? (
              <KpiSnapshot
                platforms={summary.snapshot.platforms}
                currency={summary.snapshot.currency}
                syncedAt={summary.snapshot.synced_at}
              />
            ) : (
              <SectionErrorState
                message={errorsBySection.get("snapshot")?.details || "Couldn't load the KPI snapshot."}
                onRetry={overview.snapshot.refresh}
                retrying={overview.snapshot.refreshing}
              />
            )}
          </SectionShell>
        }
        right={
          SHOW_TOP_MOVERS ? (
            <SectionShell
              title="Top movers"
              syncedAt={summary.top_movers?.synced_at ?? summary.synced_at}
              refreshing={overview.topMovers.refreshing}
              onRefresh={overview.topMovers.refresh}
            >
              {summary.top_movers ? (
                <TopMovers movers={summary.top_movers.movers} comparisonLabel="vs last 7 days" />
              ) : (
                <SectionErrorState
                  message={errorsBySection.get("top_movers")?.details || "Couldn't load top movers."}
                  onRetry={overview.topMovers.refresh}
                  retrying={overview.topMovers.refreshing}
                />
              )}
            </SectionShell>
          ) : null
        }
      />

      <Separator />

      {/* --- Insights & Recommendations -------------------------------- */}
      <InsightsSection
        userId={user.id}
        organizationId={organization.id}
        userEmail={user.email}
      />

      <Separator />

      {/* --- External signals ------------------------------------------ */}
      <SectionShell
        title="External signals"
        rightLabel="factors outside your campaigns that can move your numbers"
        refreshing={overview.externalSignals.refreshing}
        onRefresh={overview.externalSignals.refresh}
      >
        {summary.external_signals ? (
          <ExternalSignals
            provider={summary.external_signals.provider}
            signals={summary.external_signals.signals}
          />
        ) : (
          <SectionErrorState
            message={errorsBySection.get("external_signals")?.details || "Couldn't load external signals."}
            onRetry={overview.externalSignals.refresh}
            retrying={overview.externalSignals.refreshing}
          />
        )}
      </SectionShell>
    </PageFrame>
  );
}

// --- layout primitives --------------------------------------------------

interface PageFrameProps {
  children: React.ReactNode;
  syncedAt?: string;
  isFetching?: boolean;
  onRefetch?: () => void;
}

/**
 * Slim page frame — drops the heavy v1 PageHeader because each section
 * already has its own title row. We just keep a tiny "Last synced · refresh"
 * strip in the top-right so the page-level state is observable.
 */
function PageFrame({ children, syncedAt, isFetching, onRefetch }: PageFrameProps) {
  return (
    <section className="w-full space-y-6">
      {(syncedAt || onRefetch) && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          {syncedAt && <span>Last synced {formatRelativeUpdated(syncedAt)}</span>}
          {onRefetch && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={onRefetch}
              disabled={isFetching}
            >
              {isFetching ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="size-3.5" />
              )}
              <span className="ml-1.5">Refetch all</span>
            </Button>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Two-column responsive layout for the snapshot + top movers row. We give
 * the right column proportionally more room than first instinct suggests
 * (~45%) because the mover rows carry long entity names ("UAC | SegA |
 * Kannada | Eng & Kannada | KA") plus an italic annotation plus a magnitude
 * bar plus a delta — all of which truncate aggressively on narrower tracks.
 * Snapshot stays ~55% which is plenty for the 4-row × 4-col table.
 *
 * When `right` is null (e.g. Top movers feature-flagged off) the snapshot
 * goes full-width and the column divider is dropped so the layout reads
 * as a single section rather than a half-empty row.
 */
function TopRow({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  if (!right) {
    return <div className="min-w-0">{left}</div>;
  }
  return (
    <div className="grid gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      <div className="min-w-0">{left}</div>
      <div className="min-w-0 lg:border-l lg:pl-8">{right}</div>
    </div>
  );
}

function NoPlatformEmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="space-y-3 p-8 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted">
          <ShieldAlert className="size-6 text-muted-foreground" />
        </div>
        <h3 className="text-base font-semibold">Connect an ad account to get started</h3>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          {message ||
            "We couldn't reach any ad platform. Connect Google Ads or Meta to see your KPIs, movers, and AI-detected issues."}
        </p>
      </CardContent>
    </Card>
  );
}

// --- params -------------------------------------------------------------

/**
 * Static slice of the overview params. `customer_id` / `google_customer_id`
 * are the same hardcoded Google Ads customer id until the account picker is wired. Mirrors:
 *
 *   GET $BASE/?customer_id=7948522814&google_customer_id=7948522814&currency=INR&country=IN
 *
 * Hoisted to a module-level constant so React StrictMode double-renders,
 * HMR swaps, and parent re-renders all see the exact same object
 * reference — keeps the cache key stable across renders.
 *
 * TODO: derive both ids from `AdsIntelligenceApiFactory.Queries.FetchGoogleAdsCustomers`
 * (same lookup as the chat page account selector).
 */
const OVERVIEW_V2_PARAMS: OverviewV2ContextParams = {
  customer_id: "7948522814",
  google_customer_id: "7948522814",
  currency: "INR",
  country: "IN",
  // Bumped from the backend default (6) — the LLM candidate pool is up to
  // 8 per platform and the extra row gives the fair-share merger more
  // headroom to surface a slice when accounts/campaigns dominate.
  max_movers: 7,
};
